// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps, DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateSearchFields from "@rc/App/Handlers/Pages/BackOrderList/UpdateSearchFields";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { BackOrderDataViewContext } from "@rc/App/Store/Selectors/BackOrderSelector";
import parse from "html-react-parser";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    getBackOrderApiParameter: state.xBackOrderListState.getBackOrderApiParameter,
    language: state.context.session.language,
    BackOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BackOrderListTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    skuHeader?: DataTableHeaderPresentationProps;
    qtyHeader?: DataTableHeaderPresentationProps;
    orderNumberHeader?: DataTableHeaderPresentationProps;
    orderDateCell?: DataTableCellBaseProps;
    shipToLabelCell?: DataTableCellBaseProps;
    subtotalCell?: DataTableCellBaseProps;
    orderNameHeader?: DataTableHeaderProps;
    orderSkuHeader?: DataTableHeaderProps;
    orderQtyHeader?: DataTableHeaderProps;
}

export const backOrderListTableStyles: BackOrderListTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-bottom: 12px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px 20px 100px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
};

const styles = backOrderListTableStyles;

const BackOrderListTable = ({
    getBackOrderApiParameter,
    updateSearchFields,
    language,
    BackOrderDetailsLink,
}: Props) => {
    const headerClick = (sortField: string) => {
        const sort = getBackOrderApiParameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (getBackOrderApiParameter.sort === sortField) {
            sorted = "ascending";
        } else if (getBackOrderApiParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const backOrderListDataView = useContext(BackOrderDataViewContext);
    if (!backOrderListDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (backOrderListDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No back orders found.")}</Typography>
            </StyledWrapper>
        );
    }

    const rows = backOrderListDataView.value.map(backOrder => {
        return {
            id: backOrder.id,
            productTitle: backOrder.productTitle,
            sku: backOrder.sku,
            qty: backOrder.quantityOrdered,
            orderNumber: backOrder.orderNumber,
            orderDate: backOrder.orderDate
                ? getLocalizedDateTime({
                    dateTime: new Date(backOrder.orderDate),
                    language,
                    options: { year: "2-digit", month: "numeric", day: "numeric" },
                })
                : "",
            timeFrame: backOrder.timeFrameAvailability,
        };
    });

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader
                        sorted={sorted("productTitle")}
                        onSortClick={() => headerClick("productTitle")}
                        {...styles.orderNameHeader}
                    >
                        {translate("Name")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("sku")}
                        onSortClick={() => headerClick("sku")}
                        {...styles.orderSkuHeader}
                    >
                        {translate("SKU")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("quantityOrdered")}
                        {...styles.orderQtyHeader}
                        onSortClick={() => headerClick("quantityOrdered")}
                    >
                        {translate("QTY")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("orderNumber")}
                        {...styles.orderNumberHeader}
                        onSortClick={() => headerClick("orderNumber")}
                    >
                        {translate("Order #")}
                    </DataTableHeader>
                    <DataTableHeader
                        sorted={sorted("orderDate")}
                        {...styles.orderNumberHeader}
                        onSortClick={() => headerClick("orderDate")}
                    >
                        {translate("Order Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.orderNumberHeader}
                    >
                        {translate("Timeframe Availability")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(({ id, productTitle, sku, qty, orderNumber, orderDate, timeFrame }) => (
                        <DataTableRow
                            data-test-selector="backOrderList_orderLine"
                            data-test-key={id}
                            key={id}
                            {...styles.dataTableRow}
                        >
                            <DataTableCell {...styles.subtotalCell}>{parse(productTitle)}</DataTableCell>
                            <DataTableCell {...styles.subtotalCell}>{sku}</DataTableCell>
                            <DataTableCell {...styles.subtotalCell}>{qty}</DataTableCell>
                            <DataTableCell {...styles.subtotalCell}>{orderNumber}</DataTableCell>
                            <DataTableCell {...styles.subtotalCell}>{orderDate}</DataTableCell>
                            <DataTableCell {...styles.subtotalCell}>{timeFrame}</DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BackOrderListTable),
    definition: {
        group: "XNGAGE" as any,
        displayName: "BackOrder - Search Results Table",
    },
};

export default widgetModule;
